import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Attributes } from './components/Attributes';
import { PairsWith } from './components/PairsWith';
import { ProductCard } from './components/ProductCard';
import { RecreateNameButton } from './components/RecreateNameButton';
import { RerunButton } from './components/RerunButton';

import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useGetProduct } from '@/hooks/useGetProduct';

export function VisionProduct() {
  const { productId } = useParams();

  const { user } = useAuth();

  const { t } = useTranslation();

  const formattedProductId = productId ? +productId : 0;

  const { product, isLoading } = useGetProduct(formattedProductId);

  const productLink =
    product?.link && product.link.startsWith('http')
      ? product.link
      : user?.customer.url && product?.link
        ? `${user.customer.url}${product.link}`
        : undefined;

  const productImages = [
    product?.image1,
    product?.image2,
    product?.image3,
    product?.image4,
    product?.image5,
    product?.image6,
    product?.image7,
    product?.image8,
    product?.image9,
    product?.image10,
  ].filter(Boolean) as string[];

  if (isLoading) {
    return null;
  }

  if (!product && !isLoading) {
    return <Navigate to={ROUTE_PATHS.VISION_ENRICHMENT} replace />;
  }

  return (
    <div className="py-8">
      <Link to={ROUTE_PATHS.VISION_ENRICHMENT} className="mb-2 flex gap-1">
        <ChevronLeft />
        <span className="underline">{t('pages.VisionProduct.back')}</span>
      </Link>

      <article className="flex gap-[58px]">
        <div className="w-[320px]">
          <div className="w-[320px] space-y-4">
            <ProductCard
              product={product}
              images={productImages}
              link={productLink}
            />

            <PairsWith product={product} />
          </div>
        </div>

        <section className="w-[730px]">
          <h1 className="text-3xl">{product?.name}</h1>
          <span className="text-sm">{product?.visionCategoryName}</span>

          {product?.visionOutput && (
            <div className="mt-10 space-y-2">
              <div className="flex items-center justify-end gap-2">
                <RecreateNameButton catalogId={product.id} />

                <RerunButton
                  catalogId={product.id}
                  visionStatus={product.visionStatus}
                />
              </div>

              <Attributes
                productId={formattedProductId}
                visionCategory={product?.visionCategoryName}
                attributes={product?.visionOutput.product_attributes}
              />
            </div>
          )}
        </section>
      </article>
    </div>
  );
}
